<script setup lang="ts">
import { computed } from "vue";
import useSelectionControl, {
  type ISelectionControlEmit,
} from "../../composables/useSelectionControl";
import type { ICheckboxProps, ICheckboxEmits } from "./VCheckbox.types";

const props = withDefaults(defineProps<ICheckboxProps>(), {
  wrapperClass: undefined,
  value: true,
});
const emit = defineEmits<ICheckboxEmits>();
defineOptions({ inheritAttrs: false });

const rootClass = computed(() => [
  props.wrapperClass,
  {
    "pl-checkbox_disabled": props.disabled,
    "pl-checkbox_error": props.hasError,
  },
]);

const { onChange, isChecked } = useSelectionControl(
  props,
  emit as ISelectionControlEmit,
);
</script>

<template>
  <label class="pl-checkbox" :class="rootClass">
    <span class="pl-checkbox__container">
      <input
        v-bind="$attrs"
        :disabled="disabled"
        :checked="isChecked"
        class="pl-checkbox__field"
        type="checkbox"
        @change="onChange"
      />
      <span class="pl-checkbox__decorator">
        <svg
          v-if="indeterminate"
          width="10"
          height="2"
          viewBox="0 0 10 2"
          stroke="currentColor"
          fill="none"
        >
          <path
            d="M1 1H9"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <svg
          v-else
          width="10"
          height="8"
          viewBox="0 0 10 8"
          stroke="currentColor"
          fill="none"
        >
          <path
            d="M1 4L4 7L9 1.5"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </span>
    <span v-if="$slots.default" class="pl-checkbox__text">
      <slot />
    </span>
  </label>
</template>

<style lang="postcss" scoped>
.pl-checkbox {
  display: inline-flex;
  cursor: pointer;
  user-select: none;
  vertical-align: top;

  &_disabled {
    cursor: not-allowed;
  }
}

.pl-checkbox__container {
  position: relative;
  padding: 3px;
}

.pl-checkbox__field {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  margin: 0;
  opacity: 0;
  z-index: 1;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
}

.pl-checkbox__decorator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background-color: var(--pl-push-control-background-checkbox-off-default);
  border: 2px solid var(--pl-push-control-border-checkbox-off-default);
  border-radius: var(--pl-checkbox-radius-s);
  transition: 0.15s ease-in-out;

  & svg {
    color: var(--pl-push-control-fill-check);
    opacity: 0;
    transform: scale(0);
    transition: 0.2s transform ease-in-out;
  }

  .pl-checkbox_error & {
    background-color: var(--pl-push-control-background-checkbox-off-default);
    border-color: var(--pl-system-error-accent-default);
  }

  .pl-checkbox:hover & {
    background-color: var(--pl-push-control-background-checkbox-off-hover);
    border-color: var(--pl-push-control-border-checkbox-off-hover);
  }

  .pl-checkbox:active & {
    background-color: var(--pl-push-control-background-checkbox-off-pressed);
    border-color: var(--pl-push-control-border-checkbox-off-pressed);
  }

  .pl-checkbox__field:focus-visible + & {
    outline: 2px solid var(--pl-tabulation-border-focused);
    outline-offset: 2px;
  }

  .pl-checkbox__field:disabled + & {
    background-color: var(--pl-push-control-background-checkbox-off-disabled);
    border-color: var(--pl-push-control-border-checkbox-off-disabled);
  }

  .pl-checkbox__field:checked + & {
    background-color: var(--pl-push-control-background-on-default);
    border-color: var(--pl-push-control-background-on-default);

    & svg {
      opacity: 1;
      transform: scale(1);
    }
  }

  .pl-checkbox__field:disabled:checked + & {
    border-color: var(--pl-push-control-background-on-disabled);
    background-color: var(--pl-push-control-background-on-disabled);
  }

  .pl-checkbox:hover .pl-checkbox__field:checked + & {
    background-color: var(--pl-push-control-background-on-hover);
    border-color: var(--pl-push-control-background-on-hover);
  }

  .pl-checkbox_error:hover .pl-checkbox__field:not(:disabled) + & {
    background-color: var(--pl-push-control-background-checkbox-off-default);
    border-color: var(--pl-system-error-accent-default);
  }

  .pl-checkbox_error .pl-checkbox__field:checked:not(:disabled) + &,
  .pl-checkbox_error:hover .pl-checkbox__field:checked + & {
    background-color: var(--pl-system-error-accent-default);
    border-color: var(--pl-system-error-accent-default);
  }

  .pl-checkbox:hover .pl-checkbox__field:active:checked + & {
    background-color: var(--pl-push-control-background-on-pressed);
    border-color: var(--pl-push-control-background-on-pressed);
  }

  .pl-checkbox_disabled:hover .pl-checkbox__field:disabled:checked + & {
    border-color: var(--pl-push-control-background-on-disabled);
    background-color: var(--pl-push-control-background-on-disabled);
  }

  .pl-checkbox_error:hover
    .pl-checkbox__field:active:checked:not(:disabled)
    + & {
    background-color: var(--pl-system-error-accent-default);
    border-color: var(--pl-system-error-accent-default);
  }
}

.pl-checkbox__text {
  margin-left: var(--pl-unit-x2);
  font: var(--pl-font-body-large-regular);
}
</style>
